// Copyright (c) StrangeBee 2024

import React from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import {
    OrganisationOutput,
    UserOutput,
    OrganisationLinksOutput,
    SharingProfileOutput,
    SharingRuleValues,
} from 'thehive-sdk';
import { OrganisationEventList } from '@Organisms/Organisation/eventList';
import UploadAvatar from '@Atoms/UploadAvatar/UploadAvatar';
import { UserEventList } from '@Organisms/User/eventList';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { Space } from 'antd';
import { UserCreate } from '@Organisms/User/UserCreate/UserCreate';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { OrganisationLinkManage } from '../OrganisationLinkManage/OrganisationLinkManage';
import { OrganisationUserList } from '../OrganisationUserList/OrganisationUserList';
import { OrganisationLinkList } from '../OrganisationLinkList/OrganisationLinkList';

import './extractData.scss';

interface ExtractDataProps {
    data: OrganisationOutput;
    hasPermissions: boolean;
    eventList: OrganisationEventList;
    userList: UserOutput[];
    userEventList: UserEventList;
    locale: (label: string, params?: any) => string;
    onDrawerVisible: (visible: boolean) => void;
}

export const extractData = ({
    data,
    hasPermissions,
    userList,
    userEventList,
    eventList,
    locale,
    onDrawerVisible,
}: ExtractDataProps): Record<any, ExtractProps> => ({
    avatar: {
        className: 'organisation-drawer__avatar',
        isForce: true,
        render: () => (
            <THForm.Item name="avatar">
                <UploadAvatar />
            </THForm.Item>
        ),
    },
    name: {
        className: 'organisation-drawer__name',
        isForce: true,
        render: () => (
            <THForm.Item
                name="name"
                label={locale('common.name')}
                rules={[
                    {
                        required: true,
                        message: locale('organisation.create.form.name.required'),
                    },
                ]}
            >
                <THInput borderless disabled={!hasPermissions} />
            </THForm.Item>
        ),
    },
    locked: {
        className: 'organisation-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="locked" label={locale('common.locked')} valuePropName="checked">
                <THSwitch />
            </THForm.Item>
        ),
    },
    taskRule: {
        className: 'organisation-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="taskRule" label={locale('organisation.create.form.task-rule.label')}>
                <THSelect
                    options={SharingRuleValues.map((value) => ({ label: value, value }))}
                    placeholder={locale('organisation.create.form.task-rule.placeholder')}
                    disabled={!hasPermissions}
                    borderless
                />
            </THForm.Item>
        ),
    },
    observableRule: {
        className: 'organisation-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="observableRule" label={locale('organisation.create.form.observable-rule.label')}>
                <THSelect
                    options={SharingRuleValues.map((value) => ({ label: value, value }))}
                    placeholder={locale('organisation.create.form.observable-rule.placeholder')}
                    disabled={!hasPermissions}
                    borderless
                />
            </THForm.Item>
        ),
    },
    description: {
        className: 'organisation-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="description" label={locale('task.details.description')}>
                <THTextArea borderless disabled={!hasPermissions} placeholder="Enter a description" />
            </THForm.Item>
        ),
    },
    '#computeUsers': {
        className: 'organisation-drawer__user-list',
        isForce: true,
        name: (
            <Space>
                {locale('common.users')}

                <UserCreate
                    onFinish={(user) => userEventList.onCreate(user)}
                    orgId={data.name}
                    userList={userList}
                    triggerElem={<THButton type="link">{locale('general.add')}</THButton>}
                    onVisibleChange={onDrawerVisible}
                />
            </Space>
        ),
        render: ({ userList, userEventList }: { userList: UserOutput[]; userEventList: UserEventList }) => (
            <OrganisationUserList organisation={data} userEventList={userEventList} userList={userList} />
        ),
    },
    '#computeLinks': {
        name: (
            <Space>
                {locale('common.links')}{' '}
                <OrganisationLinkManage
                    triggerElem={<THButton type="link">{locale('organisation.links.manage')}</THButton>}
                    onListLinkableOrgs={eventList['onListLinkableOrganisations']}
                    onListSharingProfiles={eventList['onListSharingProfiles']}
                    onSetLinks={eventList['onSetLinks']}
                    orgId={data.name}
                    currentOrganisation={data}
                    onVisibleChange={onDrawerVisible}
                />
            </Space>
        ),
        className: 'organisation-drawer__links-list',
        render: ({
            links,
            orgList,
            sharingProfileList,
        }: {
            links: OrganisationLinksOutput[];
            orgList: OrganisationOutput[];
            sharingProfileList: SharingProfileOutput[];
        }) => {
            return (
                <OrganisationLinkList
                    data={data}
                    links={links}
                    orgList={orgList}
                    sharingProfileList={sharingProfileList}
                />
            );
        },
    },
});
